import React, { useEffect, useState } from "react";
import { Empty } from "../../../components/ui";
import UserService from "../../../services/user.service";
import { toast } from "react-toastify";
import { formatDate } from "../../../components/custom-hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/rootReducer";
import { useNavigate } from 'react-router-dom';
import { FileText } from "lucide-react";

export default function Reports() {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const userService = new UserService();
    const [childrenArr, setChildrenArr] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const getChildren = async () => {
        try {
            const response = await userService.getAllChildReportById(userInfo.id);
            if (!response.status) return;
            setChildrenArr(response.data);
            setLoading(false);
        } catch (err: any) {
            toast.error(err?.message);
        }
    };

    const handleClicked = async (id: any) => {
        try {
            await userService.clickedReport(id);
        } catch {
            toast.error("Failed to submit comment");
        }
    };

    const handleReport = (data: any) => {
        navigate(`/parent/report/preview/${data.id}`);
        if (!data.clicked) handleClicked(data.id);
    };

    useEffect(() => {
        getChildren();
    }, []);

    const rows = childrenArr?.slice().reverse() ?? [];

    if (loading) {
        return (
            <div className="flex h-40 items-center justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border border-ryd-primary border-l-white" />
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="rounded-[18px] border border-dashed border-[#e8dce8] bg-[#FBF8FC] px-6 py-16 text-center">
                <Empty text="You have no recent child report" />
            </div>
        );
    }

    return (
        <>
            <div className="hidden overflow-hidden rounded-[18px] border border-[#efe6f1] lg:block">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] text-left">
                        <thead>
                            <tr className="border-b border-[#efe6f1] bg-[#FBF8FC] text-[12px] font-semibold uppercase tracking-wide text-[#8b9aab]">
                                <th className="px-4 py-3">First Name</th>
                                <th className="px-4 py-3">Last Name</th>
                                <th className="px-4 py-3">Age</th>
                                <th className="px-4 py-3">Level</th>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item: any, index: number) => (
                                <tr key={item?.id ?? index} className="border-b border-[#f3ebf3] text-[14px] text-[#334155]">
                                    <td className="px-4 py-3 capitalize">{item?.child?.firstName}</td>
                                    <td className="px-4 py-3 capitalize">{item?.child?.lastName}</td>
                                    <td className="px-4 py-3">{item?.child?.age}</td>
                                    <td className="max-w-[180px] truncate px-4 py-3">{item?.program?.package?.title}</td>
                                    <td className="px-4 py-3 text-[13px] text-[#8b9aab]">{formatDate(item?.createdAt)}</td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                                item?.programs?.length === 0
                                                    ? 'bg-amber-50 text-amber-800'
                                                    : 'bg-emerald-50 text-emerald-700'
                                            }`}
                                        >
                                            {item?.programs?.length === 0 ? 'Incomplete' : 'Completed'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            type="button"
                                            onClick={() => handleReport(item)}
                                            className="inline-flex items-center gap-1.5 rounded-[10px] border border-ryd-primary/20 bg-ryd-primaryLess1 px-3 py-1.5 text-[12px] font-semibold text-ryd-primary transition hover:bg-ryd-primary hover:text-white"
                                        >
                                            <FileText size={13} /> View report
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="space-y-3 lg:hidden">
                {rows.map((item: any, index: number) => (
                    <div
                        key={`r-${item?.id ?? index}`}
                        className="rounded-[18px] border border-[#efe6f1] bg-white p-4 shadow-sm"
                    >
                        <div className="mb-3 flex justify-between gap-2">
                            <div className="min-w-0">
                                <p className="truncate font-[AvertaStd-Semibold] text-[15px] capitalize text-ryd-headerTextPrimary">
                                    {item?.child?.firstName} {item?.child?.lastName}
                                </p>
                                <p className="mt-1 text-[13px] text-[#8b9aab]">
                                    {item?.program?.package?.title} · Age {item?.child?.age}
                                </p>
                                <p className="mt-0.5 text-[12px] text-[#94a3b8]">{formatDate(item?.createdAt)}</p>
                            </div>
                            <span
                                className={`h-fit shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    item?.programs?.length === 0
                                        ? 'bg-amber-50 text-amber-800'
                                        : 'bg-emerald-50 text-emerald-700'
                                }`}
                            >
                                {item?.programs?.length === 0 ? 'Incomplete' : 'Completed'}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleReport(item)}
                            className="inline-flex w-full items-center justify-center gap-1.5 rounded-[12px] border border-ryd-primary/20 bg-ryd-primaryLess1 px-3 py-2.5 text-[13px] font-semibold text-ryd-primary"
                        >
                            <FileText size={14} /> View report
                        </button>
                    </div>
                ))}
            </div>
        </>
    );
}
